import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ContentFieldDto,
  DesignTestInputDto,
  DesignTestOutputDto
} from '../../../models/job-request-iterations-dto.interface';

interface ApiFluidLossReportValues {
  filtrateVolume?: number;
  cakeThickness?: number;
}

@Component({
  selector: 'app-api-fluid-loss-test-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './api-fluid-loss-test-report.component.html',
  styleUrl: './api-fluid-loss-test-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApiFluidLossTestReportComponent {
  @Input({ required: true }) test!: DesignTestInputDto;
  @Input({ required: true }) output!: DesignTestOutputDto;

  protected apiFluidLossReportValues(): ApiFluidLossReportValues | null {
    const filtrateVolume = this.numberValue([
      'filtratevolume',
      'filtrate30min',
      'filtrate'
    ], this.output) ?? this.numberValue([
      'filtratevolume',
      'filtrate30min',
      'filtrate'
    ], this.test);
    const cakeThickness = this.numberValue([
      'cakethickness',
      'cake32nds',
      'cake32ndsofaninch'
    ], this.output) ?? this.numberValue([
      'cakethickness',
      'cake32nds',
      'cake32ndsofaninch'
    ], this.test);

    if (filtrateVolume === undefined && cakeThickness === undefined) return null;

    return { filtrateVolume, cakeThickness };
  }

  protected formatFiltrateVolume(value: number | undefined): string {
    return value === undefined ? '—' : value.toFixed(1);
  }

  protected formatCakeThickness(value: number | undefined): string {
    return value === undefined ? '—' : value.toFixed(0);
  }

  private numberValue(names: string[], source: DesignTestInputDto | DesignTestOutputDto): number | undefined {
    return this.toNumber(this.value(names, source));
  }

  private value(names: string[], source: DesignTestInputDto | DesignTestOutputDto): string | null {
    const acceptedNames = new Set(names);
    const fields = 'parsedValues' in source ? source.parsedValues : source.parsedInputValues;
    const parsedField = fields.find(field =>
      acceptedNames.has(this.normalize(field.label)));
    return parsedField?.value?.trim() || this.xmlValue(source.content, acceptedNames);
  }

  private xmlValue(content: string | null, names: Set<string>): string | null {
    if (!content) return null;

    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.querySelector('parsererror')) return null;

    for (const element of Array.from(document.getElementsByTagName('*'))) {
      if (element.localName.toLowerCase() === 'parameter') {
        if (!names.has(this.normalize(element.getAttribute('key')))) continue;
      } else if (!names.has(this.normalize(element.localName))) {
        continue;
      }

      if (element.children.length === 0) {
        const value = element.textContent?.trim();
        if (value) return value;
      }
    }

    return null;
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').replace(/[\s_\-/,().:%]+/g, '').toLowerCase();
  }

  private toNumber(value: string | null): number | undefined {
    if (!value) return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}